import { useEffect, useRef } from 'react';
import { DATA_ROOM_HEARTBEAT_INTERVAL_MS, SpaceKind } from 'shared';

import { BackendRoute } from '../../../config/index';
import { ApiService } from '../../../services/index';
import { FilePreviewStore } from '../store/file-preview.store';

/**
 * Tells the server the visitor still has this data room in front of them, and which file of it
 * they are reading — 20.1.0 §8.
 *
 * It reports presence and nothing else: no duration, no start, no end, not even a count. How much
 * a beat is worth, which session it lands in and whether it counts at all are decided on the
 * server from its own clock, so this cannot draw itself time by beating faster or by beating after
 * the tab went away. The file is the one thing only the browser knows, and the server checks it
 * against the room before crediting anything to it.
 *
 * The clock is the tab's focus. It stops the moment focus is lost rather than at the next tick,
 * which is what makes a pause a pause, and it beats at once on the way back in so the gap the
 * server sees is the length of the pause and not of the pause plus an interval.
 */
export const useDataRoomPresence = (dataRoomId: null | string): void => {
  const previewed = FilePreviewStore(({ file }) => file);

  /**
   * The open file read through a ref, because opening one must not restart the clock: the effect
   * owns a running interval, and a dependency that changed with every preview would stop it, beat
   * at once and have the server read the restart as the visitor's return from a pause.
   *
   * A file of another space is nobody's business here — the viewer opens files from the Vault and
   * from other rooms too, and naming one of those would have the route refuse the whole beat.
   */
  const openFileId = useRef<null | string>(null);

  useEffect(() => {
    const inThisRoom =
      previewed?.space.kind === SpaceKind.DATA_ROOM && previewed.space.id === dataRoomId;

    openFileId.current = inThisRoom ? previewed.fileId : null;
  }, [dataRoomId, previewed]);

  useEffect(() => {
    if (!dataRoomId) return;

    let timer: null | ReturnType<typeof setInterval> = null;

    const stop = (): void => {
      if (!timer) return;

      clearInterval(timer);
      timer = null;
    };

    const beat = (): void => {
      void ApiService.post(
        {
          endpoint: BackendRoute.MY_DATA_ROOMS,
          routePath: [dataRoomId, 'heartbeat'],
        },
        { body: openFileId.current ? { fileId: openFileId.current } : {} },
      ).catch(() => {
        // A room that has been closed or a grant that has been withdrawn answers 403, and the page
        // the visitor is looking at will deal with that. Beating at a shut door would not.
        stop();
      });
    };

    const start = (): void => {
      if (timer || document.visibilityState !== 'visible') return;

      beat();
      timer = setInterval(beat, DATA_ROOM_HEARTBEAT_INTERVAL_MS);
    };

    const onVisibilityChange = (): void =>
      document.visibilityState === 'visible' ? start() : stop();

    start();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', start);
    window.addEventListener('blur', stop);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', start);
      window.removeEventListener('blur', stop);
    };
  }, [dataRoomId]);
};
